
(function() {
  'use strict';

  // Configuration
  // SECURITY: Add your dashboard origins to this whitelist
  // For development: include localhost URLs
  // For production: include only your production domain(s)
  const ALLOWED_ORIGINS = [
    'http://localhost:3000',
    'http://localhost:3001',
    'http://localhost:3002',
    'https://local.dev.pockla.io:3000',
    'https://local.dev.pockla.io:3002',
    'https://ywpysjkszi.eu-west-2.awsapprunner.com',
    'https://pockla.io'
  ];
  
  const DEBUG = false;
  
  // SECURITY: Set to false ONLY for development/testing
  // When false: Allows URL params (?pockla=true) and window.Pockla.enable() API
  // When true: Only allows postMessage from ALLOWED_ORIGINS (RECOMMENDED for production)
  const REQUIRE_ORIGIN_CHECK = false;

  // State
  let isEnabled = false;
  let selectedElements = [];
  let hoveredElement = null;
  let styleElement = null;
  let trustedOrigin = null; // Track the trusted dashboard origin

  // Utilities
  function log(...args) {
    if (DEBUG) console.log('[Pockla]', ...args);
  }

  function isOriginAllowed(origin) {
    return ALLOWED_ORIGINS.includes(origin);
  }

  function generateSelector(element) {
    // If element has a unique ID, use it
    if (element.id && document.querySelectorAll(`#${element.id}`).length === 1) {
      return `#${element.id}`;
    }

    // Try to build a unique selector with classes
    if (element.className && typeof element.className === 'string') {
      const classes = element.className
        .trim()
        .split(/\s+/)
        .filter(c => c && !c.startsWith('pockla-'));
      
      if (classes.length > 0) {
        const classSelector = element.tagName.toLowerCase() + '.' + classes.join('.');
        if (document.querySelectorAll(classSelector).length === 1) {
          return classSelector;
        }
      }
    }

    // Build a path with nth-of-type for precision
    const path = [];
    let current = element;

    while (current && current.tagName !== 'HTML' && current.tagName !== 'BODY') {
      let selector = current.tagName.toLowerCase();

      // Add classes if they help with uniqueness
      if (current.className && typeof current.className === 'string') {
        const classes = current.className
          .trim()
          .split(/\s+/)
          .filter(c => c && !c.startsWith('pockla-'));
        
        if (classes.length > 0) {
          const testSelector = selector + '.' + classes[0];
          const parent = current.parentElement;
          if (parent) {
            const siblingsWithClass = Array.from(parent.children).filter(
              child => child.matches(testSelector)
            );
            const siblingsWithTag = Array.from(parent.children).filter(
              child => child.tagName === current.tagName
            );
            if (siblingsWithClass.length < siblingsWithTag.length) {
              selector = testSelector;
            }
          }
        }
      }

      // Add position information for uniqueness
      const parent = current.parentElement;
      if (parent) {
        const baseSelector = selector.split(':')[0];
        const sameTypeSiblings = Array.from(parent.children).filter(
          child => child.matches(baseSelector)
        );
        if (sameTypeSiblings.length > 1) {
          const index = sameTypeSiblings.indexOf(current) + 1;
          selector += `:nth-of-type(${index})`;
        }
      }

      path.unshift(selector);
      current = current.parentElement;
    }

    // Find the shortest unique selector from the end
    for (let i = path.length - 1; i >= 0; i--) {
      const selector = path.slice(i).join(' > ');
      const matches = document.querySelectorAll(selector);
      
      if (matches.length === 1 && matches[0] === element) {
        return selector;
      }
    }

    // Return the full path
    return path.join(' > ');
  }

  function getElementInfo(element) {
    const selector = generateSelector(element);
    return {
      selector,
      tagName: element.tagName.toLowerCase(),
      textContent: (element.textContent || '').slice(0, 100),
      innerHTML: element.innerHTML.slice(0, 200),
      attributes: Array.from(element.attributes).reduce((acc, attr) => {
        acc[attr.name] = attr.value;
        return acc;
      }, {}),
      boundingRect: element.getBoundingClientRect().toJSON(),
    };
  }

  function sendMessage(type, data = {}) {
    const target = window.opener || window.parent;
    if (!target) {
      log('No target window found (opener or parent)');
      return;
    }
    
    // Use trusted origin if available, otherwise fall back to first allowed origin
    const targetOrigin = trustedOrigin || ALLOWED_ORIGINS[0];
    
    target.postMessage({
      type: `pockla:${type}`,
      data,
      url: window.location.href,
      timestamp: Date.now()
    }, targetOrigin);
    log('Sent message:', type, data, 'to origin:', targetOrigin);
  }

  // Styling
  function injectStyles() {
    if (styleElement) return;

    styleElement = document.createElement('style');
    styleElement.textContent = `
      .pockla-hover {
        box-shadow: 0 0 0 2px #A855F7 inset, 0 0 0 2px #A855F7 !important;
        cursor: pointer !important;
        transition: none !important;
      }
      .pockla-selected {
        box-shadow: 0 0 0 2px #3B82F6 inset, 0 0 0 2px #3B82F6 !important;
        transition: none !important;
      }
      .pockla-tooltip {
        position: fixed !important;
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 11px;
        font-family: monospace;
        white-space: nowrap;
        z-index: 2147483647;
        pointer-events: none;
        max-width: 400px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `;
    document.head.appendChild(styleElement);
    log('Styles injected');
  }

  function removeStyles() {
    if (styleElement) {
      styleElement.remove();
      styleElement = null;
    }
  }

  // Event Handlers
  function handleMouseOver(e) {
    if (!isEnabled) return;
    
    e.stopPropagation();
    const target = e.target;

    // Remove previous hover
    document.querySelectorAll('.pockla-hover').forEach(el => {
      el.classList.remove('pockla-hover');
    });
    
    // Remove all tooltips
    document.querySelectorAll('.pockla-tooltip').forEach(tooltip => {
      tooltip.remove();
    });

    // Add hover to current element
    if (target && target.tagName !== 'HTML' && target.tagName !== 'BODY') {
      target.classList.add('pockla-hover');
      hoveredElement = target;

      // Add tooltip with selector
      const selector = generateSelector(target);
      const tooltip = document.createElement('div');
      tooltip.className = 'pockla-tooltip';
      tooltip.textContent = selector;
      
      // Position tooltip using fixed positioning
      const rect = target.getBoundingClientRect();
      tooltip.style.left = rect.left + 'px';
      tooltip.style.top = (rect.top - 30) + 'px';
      
      document.body.appendChild(tooltip);

      sendMessage('hover', { selector });
    }
  }

  function handleMouseOut(e) {
    if (!isEnabled) return;
    
    const target = e.target;
    if (target) {
      target.classList.remove('pockla-hover');
    }
    
    // Remove all tooltips
    document.querySelectorAll('.pockla-tooltip').forEach(tooltip => {
      tooltip.remove();
    });
    
    hoveredElement = null;
    sendMessage('hover', { selector: null });
  }

  function handleClick(e) {
    if (!isEnabled) return;

    e.preventDefault();
    e.stopPropagation();
    const target = e.target;

    if (target && target.tagName !== 'HTML' && target.tagName !== 'BODY') {
      const elementInfo = getElementInfo(target);
      const selector = elementInfo.selector;
      
      // Check if already selected
      const index = selectedElements.findIndex(el => el.selector === selector);
      
      if (index >= 0) {
        // Deselect
        selectedElements.splice(index, 1);
        target.classList.remove('pockla-selected');
        sendMessage('deselect', elementInfo);
      } else {
        // Select
        selectedElements.push(elementInfo);
        target.classList.add('pockla-selected');
        target.classList.remove('pockla-hover');
        sendMessage('select', elementInfo);
      }

      // Remove all tooltips
      document.querySelectorAll('.pockla-tooltip').forEach(tooltip => {
        tooltip.remove();
      });
    }
  }

  // Control Functions
  function enable() {
    if (isEnabled) return;
    
    isEnabled = true;
    injectStyles();
    
    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('mouseout', handleMouseOut, true);
    document.addEventListener('click', handleClick, true);
    
    log('Selector enabled');
    sendMessage('ready', {
      url: window.location.href,
      title: document.title
    });
  }

  function disable() {
    if (!isEnabled) return;
    
    isEnabled = false;
    
    document.removeEventListener('mouseover', handleMouseOver, true);
    document.removeEventListener('mouseout', handleMouseOut, true);
    document.removeEventListener('click', handleClick, true);
    
    // Clear all selections and hovers
    document.querySelectorAll('.pockla-hover, .pockla-selected').forEach(el => {
      el.classList.remove('pockla-hover', 'pockla-selected');
    });
    
    // Remove all tooltips
    document.querySelectorAll('.pockla-tooltip').forEach(tooltip => {
      tooltip.remove();
    });
    
    removeStyles();
    selectedElements = [];
    hoveredElement = null;
    
    log('Selector disabled');
    sendMessage('disabled');
  }

  function clearSelections() {
    document.querySelectorAll('.pockla-selected').forEach(el => {
      el.classList.remove('pockla-selected');
    });
    selectedElements = [];
    sendMessage('cleared');
  }

  function highlightSelector(selector) {
    try {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        el.classList.add('pockla-selected');
      });
    } catch (e) {
      console.error('Invalid selector:', selector, e);
    }
  }

  function unhighlightSelector(selector) {
    try {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        el.classList.remove('pockla-selected');
      });
    } catch (e) {
      console.error('Invalid selector:', selector, e);
    }
  }

  // Message listener for dashboard commands
  window.addEventListener('message', function(event) {
    // Security: verify origin against whitelist
    if (!isOriginAllowed(event.origin)) {
      log('Blocked message from unauthorized origin:', event.origin);
      return;
    }

    // Track the trusted origin from the first valid message
    if (!trustedOrigin) {
      trustedOrigin = event.origin;
      log('Trusted origin set to:', trustedOrigin);
    }

    const { type, data } = event.data;
    log('Received message:', type, data, 'from:', event.origin);

    switch (type) {
      case 'pockla:enable':
        enable();
        break;
      case 'pockla:disable':
        disable();
        break;
      case 'pockla:clear':
        clearSelections();
        break;
      case 'pockla:highlight':
        if (data && data.selector) {
          highlightSelector(data.selector);
        }
        break;
      case 'pockla:unhighlight':
        if (data && data.selector) {
          unhighlightSelector(data.selector);
        }
        break;
      case 'pockla:ping':
        sendMessage('pong', {
          url: window.location.href,
          title: document.title,
          isEnabled
        });
        break;
    }
  });

  // Auto-enable if in standalone mode (not in iframe) - only if security is relaxed
  if (!REQUIRE_ORIGIN_CHECK && window.self === window.top) {
    // Check for URL parameter to auto-enable (DEV/DEBUG ONLY)
    const params = new URLSearchParams(window.location.search);
    if (params.get('pockla') === 'true' || params.get('pockla_enable') === 'true') {
      log('Auto-enabled via URL parameter (REQUIRE_ORIGIN_CHECK is false)');
      enable();
    }
  }

  // Expose API - only if security is relaxed (for dev/debug)
  if (!REQUIRE_ORIGIN_CHECK) {
    window.Pockla = {
      enable,
      disable,
      clearSelections,
      isEnabled: () => isEnabled,
      getSelectedElements: () => [...selectedElements],
      version: '1.0.0'
    };
    log('API exposed (REQUIRE_ORIGIN_CHECK is false)');
  } else {
    // In secure mode, only expose read-only methods
    window.Pockla = {
      isEnabled: () => isEnabled,
      getSelectedElements: () => [...selectedElements],
      version: '1.0.0'
    };
    log('API exposed (read-only mode, REQUIRE_ORIGIN_CHECK is true)');
  }

  log('Initialized', { allowedOrigins: ALLOWED_ORIGINS, secureMode: REQUIRE_ORIGIN_CHECK });

  // Send initial ready message if we're in a controlled context (popup/iframe)
  if (window.opener || window.parent !== window) {
    // Small delay to ensure parent is ready to receive messages
    setTimeout(() => {
      sendMessage('ready', {
        url: window.location.href,
        title: document.title
      });
      log('Sent initial ready message');
    }, 500);
  }
})();


(function() {
  'use strict';

  // Configuration object
  const config = {
    apiBaseUrl: 'YOUR_API_BASE_URL', // Replace with your actual API URL
    debug: true, // Set to true for console logging
    timeout: 5000, // Request timeout in milliseconds
    retryAttempts: 2, // Number of retry attempts on failure
    retryDelay: 1000, // Delay between retries in milliseconds
    antiFlicker: { // Optional anti-flicker protection
      enabled: true,
      maxDelayMs: 1200 // Maximum time to keep content hidden
    }
  };

  // Main personalization object
  window.PocklaPersonalizationPKLID = {
    config: config,
    
    /**
     * Initialize the personalization
     * @param {Object} options - Configuration options
     */
    init: function(options) {
      // Merge user options with defaults
      Object.assign(this.config, options);
      
      if (this.config.debug) {
        console.log('Pockla Personalization (PKLID): Initializing...', this.config);
      }
      
      // Get pklid from query parameters
      const pklid = this.getPKLID();
		console.log("PKLID", pklid)
      
      if (!pklid) {
        if (this.config.debug) {
          console.log('Pockla Personalization (PKLID): No pklid query parameter found, skipping personalization');
        }
        return;
      }
      
      if (this.config.debug) {
        console.log('Pockla Personalization (PKLID): Found pklid:', pklid);
      }
      
      // Optional anti-flicker: hide content briefly until personalization applied or timeout
      if (this.config.antiFlicker && this.config.antiFlicker.enabled) {
        this.enableAntiFlicker();
      }
      
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.loadPersonalizedContent(pklid));
      } else {
        this.loadPersonalizedContent(pklid);
      }
    },

    /**
     * Extract the pklid query parameter from the URL
     * @returns {string|null} The pklid value or null if not found
     */
    getPKLID: function() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('pklid');
      } catch (error) {
        if (this.config.debug) {
          console.error('Pockla Personalization (PKLID): Error getting pklid from URL', error);
        }
        return null;
      }
    },

    /**
     * Load personalized content from the API
     * @param {string} pklid - The page ID from the pklid query parameter
     */
    loadPersonalizedContent: function(pklid) {
      const url = `${this.config.apiBaseUrl}/pages/${pklid}`;
      
      if (this.config.debug) {
        console.log('Pockla Personalization (PKLID): Fetching content from', url);
      }

      this.makeRequest(url)
        .then(response => {
          if (response.status === 'OK' && response.data && response.data.contentBySelector) {
            this.applyPersonalizedContent(response.data.contentBySelector);
          } else if (response.status === 'ERROR' && response.message === 'Page not found') {
            if (this.config.debug) {
              console.log('Pockla Personalization (PKLID): No personalized content found for pklid:', pklid);
            }
          } else {
            if (this.config.debug) {
              console.warn('Pockla Personalization (PKLID): Unexpected response format', response);
            }
          }
        })
        .catch(error => {
          if (this.config.debug) {
            console.error('Pockla Personalization (PKLID): Failed to load content', error);
          }
        })
        .finally(() => {
          if (this.config.antiFlicker && this.config.antiFlicker.enabled) {
            this.disableAntiFlicker();
          }
        });
    },

    /**
     * Make HTTP request with retry logic
     * @param {string} url - The URL to fetch
     * @param {number} attempt - Current attempt number
     */
    makeRequest: function(url, attempt = 1) {
      return fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(this.config.timeout)
      })
      .then(response => {
        if (!response.ok) {
          // Don't retry for 404 - page doesn't exist
          if (response.status === 404) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .catch(error => {
        // Don't retry for 404 errors
        if (error.message.includes('HTTP 404') || attempt >= this.config.retryAttempts) {
          if (error.message.includes('HTTP 404')) {
            throw error; // Don't retry 404s
          }
          throw error; // Max retries reached
        }
        if (this.config.debug) {
          console.log(`Pockla Personalization (PKLID): Retry attempt ${attempt + 1}/${this.config.retryAttempts}`);
        }
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(this.makeRequest(url, attempt + 1));
          }, this.config.retryDelay);
        });
      });
    },

    /**
     * Apply personalized content to the page
     * @param {Array} contentBySelector - Array of content objects with selector and html
     */
    applyPersonalizedContent: function(contentBySelector) {
      if (!Array.isArray(contentBySelector)) {
        if (this.config.debug) {
          console.warn('Pockla Personalization (PKLID): contentBySelector is not an array', contentBySelector);
        }
        return;
      }

      let appliedCount = 0;

      contentBySelector.forEach(item => {
        if (!item.selector || !item.html) {
          if (this.config.debug) {
            console.warn('Pockla Personalization (PKLID): Invalid content item', item);
          }
          return;
        }

        try {
          const elements = document.querySelectorAll(item.selector);
          
          if (elements.length === 0) {
            if (this.config.debug) {
              console.log(`Pockla Personalization (PKLID): No elements found for selector: ${item.selector}`);
            }
            return;
          }

          elements.forEach(element => {
            // Replace content while preserving any existing event listeners
            element.innerHTML = item.html;
            appliedCount++;
          });

          if (this.config.debug) {
            console.log(`Pockla Personalization (PKLID): Applied content to ${elements.length} element(s) for selector: ${item.selector}`);
          }
        } catch (error) {
          if (this.config.debug) {
            console.error(`Pockla Personalization (PKLID): Error applying content for selector ${item.selector}:`, error);
          }
        }
      });

      if (this.config.debug) {
        console.log(`Pockla Personalization (PKLID): Applied ${appliedCount} content updates`);
      }
    },

    /**
     * Enable anti-flicker by temporarily hiding the body
     */
    enableAntiFlicker: function() {
      try {
        // Inject a minimal style and add a class to html to control scope
        const styleId = 'pockla-pklid-af-style';
        if (!document.getElementById(styleId)) {
          const style = document.createElement('style');
          style.id = styleId;
          style.type = 'text/css';
          style.textContent = 'html.pockla-pklid-af body{opacity:0 !important; transition:none !important}';
          document.head.appendChild(style);
        }
        document.documentElement.classList.add('pockla-pklid-af');
        const maxDelay = (this.config.antiFlicker && this.config.antiFlicker.maxDelayMs) || 1200;
        // Store timeout handle for cleanup
        this._afTimeout = window.setTimeout(() => {
          this.disableAntiFlicker();
        }, maxDelay);
      } catch (_) {
        // no-op if DOM not ready
      }
    },

    /**
     * Disable anti-flicker and cleanup style/timeout
     */
    disableAntiFlicker: function() {
      try {
        document.documentElement.classList.remove('pockla-pklid-af');
        if (this._afTimeout) {
          window.clearTimeout(this._afTimeout);
          this._afTimeout = null;
        }
      } catch (_) {
        // ignore
      }
    },

    /**
     * Manually trigger content reload with a specific pklid
     * @param {string} pklid - Optional pklid to use, defaults to query parameter
     */
    reload: function(pklid) {
      const pageId = pklid || this.getPKLID();
      if (pageId) {
        this.loadPersonalizedContent(pageId);
      } else if (this.config.debug) {
        console.warn('Pockla Personalization (PKLID): No pklid provided for reload');
      }
    },

    /**
     * Update configuration
     * @param {Object} newConfig - New configuration options
     */
    updateConfig: function(newConfig) {
      Object.assign(this.config, newConfig);
    }
  };

  // Auto-initialize if config is provided via data attributes
  const script = document.currentScript;
  if (script) {
    const apiBaseUrl = "https://api.pockla.io";
    const debug = script.getAttribute('data-debug') === 'true';

    if (apiBaseUrl) {
      window.PocklaPersonalizationPKLID.init({
        apiBaseUrl: apiBaseUrl,
        debug: debug
      });
    }
  }
})();


